/**
 * Turning a Sidekick voice pick into config-draft writes.
 *
 * A voice is not an `update_field`-shaped setting: choosing one writes the
 * default persona's `voice_preset` and `emotions_yaml`, plus the legacy
 * top-level mirrors that non-persona-aware code paths still read. That is why
 * `set_voice` is its own tool and why this cannot go through
 * `coerceFieldValue` — `personas` is an array of objects, and it isn't in the
 * Sidekick field registry at all.
 *
 * Kept as a pure function so the mapping is testable without a config page, a
 * WebSocket or an audio element. It mirrors `pages/bot/Voice.tsx: selectVoice`
 * deliberately — the two paths must produce the same config, or a bot's voice
 * would depend on which surface set it.
 */
import type { Persona } from '../../config/types';
import type { VoiceOption } from './sidekickChatMessages';

/** Just the config keys a voice write reads. */
export interface VoiceApplyInput {
  personas?: Persona[];
  emotions_yaml?: string;
  voice_gender?: string;
}

/** Draft writes, as `key → value` for `ctl.update`. */
export interface VoiceApplyResult {
  personas: Persona[];
  emotions_yaml?: string;
  voice_gender?: string;
}

/**
 * Compute the draft writes for picking `voice`.
 *
 * Two rules carried over from the config page, both load-bearing:
 *
 *  - **Provider presets (Gemini) must write an EMPTY `emotions_yaml`.** A
 *    non-empty one trips the backend's Gemini compatibility gate on save, so the
 *    bot silently falls back to Deepdub with a voice it does not have.
 *  - **Multi-persona bots only get `voice_preset` switched.** With 2+ personas
 *    the top-level `emotions_yaml`/`voice_gender` belong to the cast as a whole;
 *    overwriting them from one persona's voice would retune the others.
 *
 * `voice_gender` is mirrored from the preset, matching the config page. It is
 * grammatical gender (Hebrew/Arabic verb agreement) rather than how the voice
 * sounds, but leaving the two disagreeing is worse: the bot would refer to
 * itself with the wrong agreement in exactly the languages that notice.
 */
export function applyVoiceSelection(
  config: VoiceApplyInput,
  voice: VoiceOption,
): VoiceApplyResult {
  const emotionsYaml = voice.provider ? '' : (voice.yaml || '');
  const gender = voice.gender || 'auto';
  const personas = config.personas || [];
  // A custom voice has no catalog preset behind it: the YAML is the voice, and
  // an unknown `voice_preset` id would be rejected by the backend on save.
  const presetId = voice.custom ? '' : voice.id;

  if (personas.length === 0) {
    // No personas yet: create the default one, the way the config page does, so
    // the bot ends up in the same shape whichever surface set its voice.
    return {
      personas: [{
        // Same seed as the config page's ensureDefaultPersona, id included —
        // two surfaces minting different ids for "the default persona" would
        // make the bot's shape depend on where its voice was first set.
        id: 'persona_a',
        name: 'Default',
        role: '',
        is_default: true,
        voice_preset: presetId,
        emotions_yaml: emotionsYaml,
        voice_gender: gender,
      }],
      emotions_yaml: emotionsYaml,
      voice_gender: gender,
    };
  }

  const singleVoice = personas.length <= 1;
  const hasExplicitDefault = personas.some((p) => p.is_default);
  const next = personas.map((p, i) => {
    // With no persona flagged default, the first one is it — same fallback as
    // `selectedVoiceID` on the backend.
    const isTarget = p.is_default || (!hasExplicitDefault && i === 0);
    if (!isTarget) return p;
    return {
      ...p,
      voice_preset: presetId,
      ...(singleVoice ? { emotions_yaml: emotionsYaml, voice_gender: gender } : {}),
    };
  });

  if (!singleVoice) {
    return { personas: next };
  }
  return { personas: next, emotions_yaml: emotionsYaml, voice_gender: gender };
}
